<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Close, Loading } from '@element-plus/icons-vue'
import { useAuthStore } from '@/stores/authStore'
import { useErrorAssistStore } from '@/stores/errorAssistStore'
import { useErrorAssistDiagnosis } from '@/composables/useErrorAssistDiagnosis'
import { _ElMessage } from '@/utils/element-plus-wrapper'
import ChatMessages from '@/components/ChatMessages.vue'
import ChatInput from '@/components/ChatInput.vue'
import { buildSalesAgentSceneContext } from '@/types/scene-context.type'
import { ApiErrorKind, type ApiErrorEntry } from '@/types/api-error.type'

const errorAssistStore = useErrorAssistStore()
const authStore = useAuthStore()
const route = useRoute()

const {
  conversationId,
  isAnswerReady,
  isDiagnosisFailed,
  isDiagnosing,
  isDiagnosisThrottled,
  startDiagnosis,
  sendFollowUp,
  resetDiagnosis,
} = useErrorAssistDiagnosis()

const messages = computed(() => errorAssistStore.messages)
const activeTx = computed(() => errorAssistStore.activeTx)

const askedEntry = computed(() => errorAssistStore.askedEntry)
const sheetOpen = ref(false)
const canAsk = computed(() => authStore.is_loggedin && !route.meta.adminPage)

// --- プロンプト組み立て ---

// 1 操作で連鎖して落ちたリクエストを同一の事象とみなす窓。単発では原因が判別できないため
// まとめて渡す
const RELATED_WINDOW_MS = 5000

function describe(entry: ApiErrorEntry): string {
  const head = `${entry.statusCode ?? 'unknown'} ${entry.method} ${entry.path}`
  return entry.serverMessage ? `${head} — ${entry.serverMessage}` : head
}

function relatedErrors(entry: ApiErrorEntry): ApiErrorEntry[] {
  const base = new Date(entry.observedAt).getTime()
  return errorAssistStore.errorEntries.filter(
    (e) => e !== entry && Math.abs(new Date(e.observedAt).getTime() - base) <= RELATED_WINDOW_MS,
  )
}

function composeQuestion(entry: ApiErrorEntry): string {
  const lines = [
    'I hit an error in the app and I do not know what it means. What happened, and what should I do next?',
    '',
    '--- Error details (captured automatically) ---',
    `Status: ${entry.statusCode}`,
    `Request: ${entry.method} ${entry.path}`,
    `Time: ${entry.observedAt}`,
    `Screen: ${entry.route}`,
  ]
  if (entry.serverMessage) lines.push(`Server message: ${entry.serverMessage}`)

  const related = relatedErrors(entry)
  if (related.length > 0) {
    lines.push('', 'Other requests that failed at the same time:')
    lines.push(...related.map((e) => `- ${describe(e)}`))
  }
  return lines.join('\n')
}

// --- エラー受信と診断起動 ---

const latestError = computed(() => errorAssistStore.errorEntries[0])

watch(latestError, (entry) => {
  if (!entry || !canAsk.value) return
  // 通信断は AI に聞いても答えようがないので、記録だけしてこのバナーには出さない
  if (entry.kind === ApiErrorKind.network) return
  // 連鎖分の 2 件目以降で投げ直さない
  if (askedEntry.value !== null) return
  // cooldown 中はバナーごと出さない。直前の診断から間を置かずに会話を積まない
  if (isDiagnosisThrottled()) return
  errorAssistStore.setAskedEntry(entry)

  startDiagnosis(composeQuestion(entry), `Error ${entry.statusCode} on ${entry.path}`, {
    sceneContext: buildSalesAgentSceneContext(),
    errorDetails: entry.details,
  })
})

// --- バナー表示 ---

const dismissedWhileWorking = ref(false)

const bannerVisible = computed(
  () =>
    askedEntry.value !== null &&
    canAsk.value &&
    !sheetOpen.value &&
    !(dismissedWhileWorking.value && isDiagnosing.value),
)

const errorSummary = computed(() => {
  const entry = askedEntry.value
  if (!entry) return ''
  if (entry.kind === ApiErrorKind.server) {
    return `A server error occurred (${entry.statusCode}).`
  }
  return entry.serverMessage ?? `The request could not be completed (${entry.statusCode}).`
})

function discardDiagnosis() {
  resetDiagnosis()
  errorAssistStore.setAskedEntry(null)
  dismissedWhileWorking.value = false
  openRequested.value = false
}

// 調査中の × は「今は邪魔」であって「もう要らない」ではない。診断は走らせたまま
// 引っ込めて、結果が出たら完了の姿で出し直す
function handleDismissBanner() {
  if (isDiagnosing.value) {
    dismissedWhileWorking.value = true
    openRequested.value = false
    return
  }
  discardDiagnosis()
}

// --- シート ---

const sheetBodyRef = ref<HTMLElement | null>(null)
const followUpInput = ref('')
const isAgentWorking = computed(() => {
  const status = activeTx.value?.status
  return status === 'queued' || status === 'in_progress'
})

// バナーは conversationId が公開される前から押せるので、その間の tap は捨てずに
// 覚えておいて、揃った時点で開く
const openRequested = ref(false)

function openSheet() {
  if (conversationId.value === null) {
    openRequested.value = true
    return
  }
  sheetOpen.value = true
}

// 保留した tap の決着を 1 箇所に寄せる。質問まで投稿できたら開き、その前に診断が失敗したら
// 行き先が無いので諦める（問い合わせを促すバナーが出し直されるので、返事はそちらに任せる）
watch([conversationId, isDiagnosisFailed], ([id, failed]) => {
  if (!openRequested.value) return
  if (failed) {
    openRequested.value = false
    return
  }
  if (id === null) return
  openRequested.value = false
  sheetOpen.value = true
})

function handleFollowUp() {
  const text = followUpInput.value.trim()
  if (!text || isAgentWorking.value) return
  followUpInput.value = ''
  sendFollowUp(text, { sceneContext: buildSalesAgentSceneContext() }).catch((error) => {
    console.error('Failed to send follow-up:', error)
    _ElMessage({ type: 'error', message: 'Could not send the message.' })
  })
}

// 閉じても破棄しない。誤って外側をクリックしたときに得た回答ごと失わせないため、
// 破棄はバナーの × のみ
function closeSheet() {
  sheetOpen.value = false
  followUpInput.value = ''
}

function scrollSheetToBottom() {
  nextTick(() => {
    const el = sheetBodyRef.value
    if (el) el.scrollTop = el.scrollHeight
  })
}

// messages は push で更新されるため ref のままだと identity が変わらず発火しない。
// activeTx も polling が毎秒新しい object を入れるので、導出した boolean を見る
watch([sheetOpen, () => messages.value.length, isAgentWorking], () => {
  if (sheetOpen.value) scrollSheetToBottom()
})
</script>

<template>
  <Transition name="banner-slide">
    <div
      v-if="bannerVisible"
      class="api-error-banner"
      :class="{ 'is-ready': isAnswerReady }"
      role="alert"
    >
      <div class="banner-head">
        <button v-if="isDiagnosing" class="banner-progress" @click="openSheet">
          <el-icon class="is-loading" :size="13"><Loading /></el-icon>
          <span>AI is looking into this error…</span>
        </button>
        <p v-else-if="isAnswerReady" class="banner-title">AI looked into this error.</p>
        <p v-else class="banner-sub">AI couldn't look into this error. Please contact support.</p>
        <el-button
          class="banner-close"
          :icon="Close"
          size="small"
          circle
          aria-label="Dismiss error"
          @click="handleDismissBanner"
        />
      </div>
      <el-button
        v-if="isAnswerReady"
        class="banner-action"
        type="primary"
        size="small"
        @click="openSheet"
      >
        See what AI found
      </el-button>
    </div>
  </Transition>

  <Teleport to="body">
    <Transition name="fade">
      <div v-if="sheetOpen" class="sheet-overlay" @click.self="closeSheet">
        <Transition name="sheet-slide" appear>
          <div class="assist-sheet" role="dialog" aria-label="AI support for this error">
            <header class="sheet-header">
              <div class="sheet-heading">
                <p class="sheet-title">AI support</p>
                <p class="sheet-sub">{{ errorSummary }}</p>
              </div>
              <el-button :icon="Close" circle aria-label="Close" @click="closeSheet" />
            </header>

            <div ref="sheetBodyRef" class="sheet-body">
              <ChatMessages :messages="messages" :active-tx="activeTx" />
            </div>

            <div class="sheet-footer">
              <ChatInput
                v-model="followUpInput"
                placeholder="Ask a follow-up..."
                @send="handleFollowUp"
              />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.api-error-banner {
  position: fixed;
  /* 中央に置くと footer の主要な操作（chat input の入力欄など）の真上に乗る */
  right: 12px;
  /*
    footer を持つ画面ではその上に逃がす。--app-bottom-inset は useBottomInset が
    view 側から公開する。footer 自身が safe-area を吸っているので、inset があるときは
    env() を足さない
  */
  bottom: max(16px, env(safe-area-inset-bottom), calc(var(--app-bottom-inset, 0px) + 12px));
  z-index: var(--z-takeover);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  max-width: min(480px, calc(100vw - 24px));
  padding: 10px 12px;
  border-radius: 12px;
  /* 調査中は「置いてあるだけ」。面を赤くすると、まだ何も分かっていない段階で断定に読める */
  background: #ffffff;
  border: 1px solid var(--el-border-color);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &.is-ready {
    border-color: var(--el-color-primary);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.14);
  }

  .banner-head {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .banner-close,
  .banner-action {
    flex-shrink: 0;
  }

  .banner-title,
  .banner-sub,
  .banner-progress {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    font-size: var(--font-size-caption);
    line-height: var(--line-height-tight);
  }

  .banner-title {
    font-weight: bold;
  }

  /* div + tabindex の自作 button にせず、キーボードからも届く本物の button にしておく */
  .banner-progress {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    opacity: 0.55;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-takeover);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}

.assist-sheet {
  display: flex;
  flex-direction: column;
  width: min(560px, 100vw);
  height: 70dvh;
  background: #ffffff;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.2);
  overflow: hidden;

  .sheet-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--el-border-color);
  }

  .sheet-heading {
    flex: 1;
    min-width: 0;
  }

  .sheet-title {
    margin: 0;
    font-size: var(--font-size-body-strong);
    font-weight: bold;
    line-height: var(--line-height-tight);
  }

  .sheet-sub {
    margin: 2px 0 0;
    font-size: var(--font-size-caption);
    line-height: var(--line-height-tight);
    opacity: 0.7;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sheet-body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;
    background: #f5f5f5;
  }

  .sheet-footer {
    padding: 8px 12px max(8px, env(safe-area-inset-bottom));
    border-top: 1px solid var(--el-border-color);
  }
}

.banner-slide-enter-active,
.banner-slide-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.banner-slide-enter-from,
.banner-slide-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.sheet-slide-enter-active,
.sheet-slide-leave-active {
  transition: transform 0.24s ease;
}
.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateY(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
